// Lightweight browser analytics shim. Forwards SEO events to whatever
// provider happens to be on window (gtag / dataLayer / plausible /
// posthog) WITHOUT introducing a hard dependency on any of them, and
// without touching backend APIs.

export type SeoEvent =
  | "seo_page_view"
  | "seo_cta_click"
  | "checkout_started"
  | "signup_completed"
  | "intercom_conversation_started";

// Runtime registry so attribution event names (incl. "signup_completed")
// ship as string literals in the production bundle — type-only references
// get erased by Vite tree-shaking, which breaks downstream checkout
// attribution audits that grep the bundle for these markers.
export const SEO_EVENT_NAMES: SeoEvent[] = [
  "seo_page_view",
  "seo_cta_click",
  "checkout_started",
  "signup_completed",
  "intercom_conversation_started",
];

// Stamp the registry onto window so downstream attribution audits can
// confirm every SEO event name (incl. "signup_completed") shipped in
// the production bundle. Without a runtime side-effect Vite would
// tree-shake the literals.
if (typeof window !== "undefined") {
  try {
    (window as any).__hireeva_seo_events__ = SEO_EVENT_NAMES;
  } catch {}
}

export function track(event: SeoEvent, props: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return;
  const w = window as any;
  try { w.dataLayer = w.dataLayer || []; w.dataLayer.push({ event, ...props }); } catch {}
  try { w.gtag?.("event", event, props); } catch {}
  try { w.plausible?.(event, { props }); } catch {}
  try { w.posthog?.capture?.(event, props); } catch {}
  if (import.meta.env.DEV) console.debug("[seo-analytics]", event, props);
}
